import { DragHandleIcon } from '@chakra-ui/icons';
import { As, Box, Icon, SkeletonText } from '@chakra-ui/react';
import { CSS } from '@dnd-kit/utilities';
import React, { CSSProperties, PropsWithChildren } from 'react';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type UniqueIdentifier
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// needed for row & cell level scope DnD setup
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useResourceList, useResourceListProps } from 'hooks/useResourceList';
import { useSortItems } from './hooks/useSortItems';

// Row Component
export const DraggableRow = ({ id, children, as = 'div' }: PropsWithChildren<{ id: string, as?: As }>) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform), //let dnd-kit do its thing
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  }
  return (
    // connect row ref to dnd-kit, apply important styles
    <Box ref={setNodeRef} style={style} as={as}>
      {children}
    </Box>
  )
}

export const RowDragHandleButton = ({ rowId }: { rowId: string }) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  })
  return (
    // Alternatively, you could set these attributes on the rows themselves
    <button {...attributes} {...listeners}>
      <Icon as={DragHandleIcon} />
    </button>
  )
}

interface Row {
  id: string;
}

interface DndSortableListProps {
  resource: useResourceListProps;
  onDragStart?(event: DragStartEvent): void;
  children: ((props: {
    sortedData: Row[];
    isLoading: boolean;
  }) => React.ReactNode);
}

export default function DndSortableListResource({ resource, children, onDragStart }: DndSortableListProps) {
  const { data = [], isLoading } = useResourceList(resource);

  const { onSortItems } = useSortItems({ resourceName: resource.path || resource.resourceName });

  const [rows, setRows] = React.useState<Row[]>(data);

  const dataIds = React.useMemo<UniqueIdentifier[]>(
    () => rows ? rows.map(({ id }) => id) : [],
    [rows]
  );

  React.useEffect(() => {
    setRows(data);
  }, [data]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      const newRows = arrayMove([...rows], oldIndex, newIndex);
      onSortItems({ ids: newRows.map(({ id }) => id as string)});
      setRows(newRows);
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      onDragStart={onDragStart}
      sensors={sensors}
    >
      <SortableContext
        items={dataIds}
        strategy={verticalListSortingStrategy}
      >
        <SkeletonText isLoaded={!isLoading}>
          { children({ sortedData: rows, isLoading}) }
        </SkeletonText>
      </SortableContext>
    </DndContext>
  )
}
